import {
  StatusIconGray,
  StatusIconGreen,
  StatusIconRed,
  StatusIconYellow
} from 'assets/icons'
import { ImagesPng } from 'assets/images'
import L from 'leaflet'
import {
  ENGAGEMENT_TIME_FORMAT,
  LOGOUT_ERR_MSG,
  MAX_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  UNSUPPORTED_MAX_ZOOM_LEVEL,
  ZOOM_LEVEL,
  EventIcon
} from 'utils/constants'

import { Month } from './constants'

export const iconMarkerImage = (friend, html, isAuth) => L.divIcon({
  html,
  popupAnchor: friend === true ? [-5, -15] : [0, -15],
  iconSize: friend === true ? new L.Point(30, 49) : new L.Point(27.5, 46),
  iconAnchor: friend === true ? [15, 41] : [14, 42],
  className: 'leaflet-div-icon-marker' + (isAuth ? '' : ' loggedOut')
})

export const eventIconMarkerImage = (html) => L.divIcon({
  html,
  iconSize: new L.Point(30, 30),
  iconAnchor: [14, 20],
  popupAnchor: [0, -10],
  className: 'leaflet-event-icon-marker'
})

export const htmlTemplate = (avatar, marker, friend) => {
  let html = `<img src="${ImagesPng.DefaultImage}" width="27px" height="27px" marginTop="5px" class="avatar" alt="marker" /><img src="${marker}" width="27.5px" height="46px" class="marker-image" alt="marker" />`
  if (friend === 'accepted' && avatar !== ImagesPng.DefaultImage) {
    html = `<img src="${avatar}" width="27px" height="27px" marginTop="5px" class="avatar-friend" alt="marker" /><img src="${marker}" width="27.5px" height="46px" class="marker-image-friend" alt="marker" />`
    return html
  } if (friend === 'accepted' && avatar === null) {
    html = `<img src="${ImagesPng.DefaultImage}" width="27px" height="27px" marginTop="5px" class="avatar-friend-no-avatar" alt="marker" /><img src="${marker}" width="27.5px" height="46px" class="marker-image-friend" alt="marker" />`
    return html
  }
  return html
}

export const convertToOwnTimeZoneEvent = (origDate) => {
  const utcDate = new Date(origDate + 'Z')

  const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }))

  return localDate
}

export const createEventMarkerColor = (start_date) => {
  let color = null
  let date = convertToOwnTimeZoneEvent(start_date)
  let month = date?.getMonth() + 1
  let isTwoDigit = false

  // for event markers
  Month?.forEach((e) => {
    if (e.id === month.toString()) {
      color = e.color

      if (month.toString().length > 1) {
        isTwoDigit = true
      }

      month = month.toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false
      })
    }
  })

  return eventIconMarkerImage(EventIcon(color, month, isTwoDigit))
}

export const createMarkerColor = (
  date,
  avatar,
  friends,
  isAuth
) => {
  const lastLoginDate = date.diff(new Date(), 'week')
  const markerAvatar = avatar
  let markerColor = ImagesPng.GreenMarker

  if (!isAuth) {
    return iconMarkerImage(
      false,
      htmlTemplate(markerAvatar, ImagesPng.BlackMarker, false),
      isAuth
    )
  }

  if (lastLoginDate <= -2 && lastLoginDate > -52) {
    markerColor = ImagesPng.GoldMarker
  } else if (lastLoginDate <= -52 && lastLoginDate > -100) {
    markerColor = ImagesPng.RedMarker
  } else if (lastLoginDate <= -100) {
    markerColor = ImagesPng.BlackMarker
  }

  return iconMarkerImage(
    friends === 'accepted',
    htmlTemplate(markerAvatar, markerColor, friends),
    isAuth
  )
}

export const statusMarkerColor = (date) => {
  const lastLoginDate = date.diff(new Date(), 'week')

  if (lastLoginDate <= -2 && lastLoginDate > -52) {
    return <StatusIconYellow />
  }
  if (lastLoginDate <= -52 && lastLoginDate > -100) {
    return <StatusIconRed />
  }

  if (lastLoginDate <= -100) {
    return <StatusIconGray />
  }

  return <StatusIconGreen />
}

export const whatMarkerColor = (date) => {
  const lastLoginDate = date.diff(new Date(), 'week')

  if (lastLoginDate <= -2 && lastLoginDate > -52) {
    return 'yellow'
  }
  if (lastLoginDate <= -52 && lastLoginDate > -100) {
    return 'red'
  }

  if (lastLoginDate <= -100) {
    return 'gray'
  }

  return 'green'
}

export const getUrlParams = (fastParams, secondsParams) => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  return [params.get(fastParams), params.get(secondsParams)]
}

export const flyLocation = async (
  mapRef,
  lat = 0,
  lng = 0,
  getCenter = false,
  isStatic = true,
  teleport = false,
  origZoom = 0
) => {
  // see https://leafletjs.com/reference.html#map-setview
  let latlng = L.latLng(lat, lng)

  let zoom = origZoom
  if (!zoom) {
    if (lat >= 84.8) {
      zoom = UNSUPPORTED_MAX_ZOOM_LEVEL
    } else if (lat >= 84.6) {
      zoom = 21
    } else if (lat >= 83.5) {
      zoom = 19
    } else if (lat >= 60) {
      zoom = 7
    } else {
      zoom = isStatic ? MAX_ZOOM_LEVEL : ZOOM_LEVEL
    }
  }

  if (mapRef?.current) {
    if (getCenter && !teleport) {
      const mapCenter = mapRef.current.getCenter()

      const latDiff = Math.abs(mapCenter.lat - lat)
      const lngDiff = Math.abs(mapCenter.lng - lng)

      if (latDiff > 0.02 && lngDiff > 0.02) {
        mapRef.current.flyTo(latlng, zoom, { animate: true })
      } else {
        mapRef.current.setView(latlng, zoom, { animate: true })
      }
    } else if (teleport) {
      const currZoom = mapRef?.current?.getZoom()
      latlng = L.latLng(lat + 0.02, lng + 0.02)

      if (currZoom > 16) {
        const animationSettings = { animate: true, duration: 2.5 }
        let newZoom = currZoom / 2

        mapRef.current.flyTo(latlng, newZoom, animationSettings)

        setTimeout(() => {
          latlng = L.latLng(lat, lng)
          mapRef.current.flyTo(latlng, MIN_ZOOM_LEVEL, animationSettings)
        }, 2500)
      } else {
        mapRef.current.flyTo(latlng, zoom, { animate: true, duration: 5 })
      }
    } else {
      mapRef.current.setView(latlng, zoom, { animate: true })
    }
  }
}

// Storage Helpers
export const isAuth = () => !!JSON.parse(localStorage.getItem('credentials'))?.access_token

export const setStorage = (key, value) => localStorage.setItem(key, JSON.stringify(value))

export const getStorage = (key) => JSON.parse(localStorage.getItem(key))

export const removeStorage = (key) => localStorage.removeItem(key)

export const clearStorage = () => localStorage.clear()

export const subtractYears = (date, years) => {
  date.setFullYear(date.getFullYear() - years)
  return date
}

export const connectionCount = ({ count, isVisible }) => {
  if (!isVisible) {
    return count
  }

  if (count === 0) {
    return '0'
  }

  if (count <= 25) {
    return '0-25'
  }

  if (count > 25 && count <= 50) {
    return '25-50'
  }

  if (count > 50 && count <= 100) {
    return '50-100'
  }

  if (count > 100 && count <= 200) {
    return '100-200'
  }

  if (count > 200 && count <= 500) {
    return '200-500'
  }

  if (count > 500) {
    return '500+'
  }

  return 0
}

// export const getParsedPassword = (base64Password) => {
//   let encryptStr = CryptoJS.enc.Base64.parse(base64Password)
//   let encryptData = encryptStr.toString(CryptoJS.enc.Utf8)
//   encryptData = JSON.parse(encryptData)
//   let iv = CryptoJS.enc.Base64.parse(encryptData.iv)
//   let decrypted = CryptoJS.AES.decrypt(
//     encryptData.value,
//     CryptoJS.enc.Base64.parse(process.env.REACT_APP_DECODE_KEY),
//     {
//       iv,
//       mode: CryptoJS.mode.CBC,
//       padding: CryptoJS.pad.Pkcs7
//     }
//   )

//   decrypted = CryptoJS.enc.Utf8.stringify(decrypted)

//   const regex = /"([^"]*)"/
//   const match = decrypted.match(regex)
//   if (match && match.length > 1) {
//     return match[1]
//   }
//   return null
// }

export const getCryptoKey = (key) => window.crypto.subtle.importKey(
  'raw',
  Uint8Array.from(atob(process.env.REACT_APP_DECODE_KEY), (c) => c.charCodeAt(0)),
  { name: 'AES-CBC' },
  true,
  ['encrypt', 'decrypt']
)

// native encryption for password
export const getParsedPassword2 = (base64Password) => {
  const decoder = new TextDecoder('utf-8')

  let encryptStr = Uint8Array.from(atob(base64Password), (c) => c.charCodeAt(0))
  let encryptData = decoder.decode(encryptStr)
  encryptData = JSON.parse(encryptData)
  let iv = Uint8Array.from(atob(encryptData.iv), (c) => c.charCodeAt(0))

  let algorithm = {
    name: 'AES-CBC',
    iv
  }

  // issue at the moment
  let keyData = Uint8Array.from(atob(process.env.REACT_APP_DECODE_KEY), (c) => c.charCodeAt(0))

  return getCryptoKey(keyData)
    .then((cryptoKey) => window.crypto.subtle.decrypt(
      algorithm,
      cryptoKey,
      Uint8Array.from(atob(encryptData.value), (c) => c.charCodeAt(0))
    ))
    .then((val) => {
      let decrypted = decoder.decode(val)

      const regex = /"([^"]*)"/
      const match = decrypted.match(regex)

      return match && match[1] ? match[1] : null
    })
}

export const handleClickScroll = (scrollId) => {
  const element = document.getElementById(scrollId)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

export const createValidFormatData = (data) => {
  const validQuoteData = []
  // eslint-disable-next-line guard-for-in
  for (let key in data) {
    validQuoteData.push({ id: key, value: data[key] })
  }
  return validQuoteData
}

export const howManyDayAgo = (date) => {
  const millisecondsPerDay = 86400000
  const date1 = new Date(date)
  const date2 = new Date()
  const diffInMilliseconds = date2 - date1
  const diffInDays = Math.round(diffInMilliseconds / millisecondsPerDay)

  return diffInDays > 0 ? `${diffInDays} days ago` : 'Today'
}

export const generateConverseJID = (data) => {
  const profileTypeCode = {
    friends: 0,
    dating: 1,
    professional: 2
  }

  return `${data.id}-${profileTypeCode[data.profile_type]}@${process.env.REACT_APP_XMPP_CHAT_URL}`
}

export const generateDifferenceByDay = (date) => {
  // Convert the "sent_at" timestamp to a Date object
  const sentAt = new Date(date)

  // Get the current date and time
  const now = new Date()

  // Calculate the difference between "sent_at" and the current time in milliseconds
  const timeDiff = now.getTime() - sentAt.getTime()

  // Calculate the difference in days
  const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24))

  // Define some constants for the time intervals
  const ONE_DAY = 1000 * 60 * 60 * 24
  const ONE_WEEK = ONE_DAY * 7

  // Determine the appropriate string to display based on the time difference
  let dateString
  if (daysDiff === 0) {
    dateString = 'Today'
  } else if (daysDiff === 1) {
    dateString = 'Yesterday'
  } else if (timeDiff < ONE_WEEK) {
    dateString = sentAt.toLocaleDateString()
  } else {
    // dateString = `${sentAt.toLocaleDateString()} ${sentAt.toLocaleTimeString()}`
    dateString = `${sentAt.toLocaleDateString()}`
  }
  return dateString
}

export const hideCounter = () => {
  const ZoomCont = document.querySelector('.leaflet-control-zoom')
  const filterData = document.querySelector('.filter-data')
  const markerCountBox = document.querySelector('.marker-count')
  // const eventOverlayBox = document.querySelector('.event-overlay-container')
  if (ZoomCont) {
    ZoomCont.style.opacity = 0
    ZoomCont.style.pointerEvents = 'none'
    // Force Safari repaint
    ZoomCont.style.transform = 'translateZ(0)'
    ZoomCont.style.WebkitTransform = 'translateZ(0)'
  }
  if (filterData) {
    filterData.style.opacity = 0
    filterData.style.pointerEvents = 'none'
  }
  if (markerCountBox) {
    markerCountBox.style.opacity = 0
    markerCountBox.style.pointerEvents = 'none'
  }
  // eventOverlayBox.style.opacity = 0
}

export const showCounter = () => {
  const ZoomCont = document.querySelector('.leaflet-control-zoom')
  const filterData = document.querySelector('.filter-data')
  const markerCountBox = document.querySelector('.marker-count')
  // const eventOverlayBox = document.querySelector('.event-overlay-container')
  if (ZoomCont) {
    ZoomCont.style.opacity = 1
    ZoomCont.style.pointerEvents = 'auto'
    // Force Safari repaint
    ZoomCont.style.transform = 'translateZ(0)'
    ZoomCont.style.WebkitTransform = 'translateZ(0)'
  }
  if (filterData) {
    filterData.style.opacity = 1
    filterData.style.pointerEvents = 'auto'
  }
  if (markerCountBox) {
    markerCountBox.style.opacity = 1
    markerCountBox.style.pointerEvents = 'auto'
  }
  // eventOverlayBox.style.opacity = 1
}

// NOTE: downloadable app does not know hosted url.
// window.location.href returns local file with file:// protocol
// for sharing url outside the application we use the translation
export const translateFromLocalUrl = () => {
  const newUrl = new URL(window.location.href)
  return process.env.REACT_APP_FRONTEND_URL + '/' + newUrl.search
}

export const checkForceLogout = (errMsg) => {
  let goLogOut = 0
  LOGOUT_ERR_MSG.forEach((index) => {
    if (errMsg.includes(index)) {
      goLogOut = 1
    }

    return false
  })

  return goLogOut
}

export const resizeImage = (image, useOn, callback) => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  let newWidth = 0
  let newHeight = 0
  let maxDimension = 500

  if (useOn === 'cp') {
    maxDimension = 600
  } else if (useOn === 'pp') {
    maxDimension = 550
  } else if (useOn === 'b') {
    maxDimension = 1500
  }

  const scale = Math.min(maxDimension / image.width, maxDimension / image.height)
  newWidth = image.width * scale
  newHeight = image.height * scale

  canvas.width = newWidth
  canvas.height = newHeight
  ctx.drawImage(
    image,
    0,
    0,
    image.width,
    image.height,
    0,
    0,
    newWidth,
    newHeight
  )

  canvas.toBlob((blob) => {
    if (blob) {
      callback(blob)
    } else {
      console.error('Failed to create Blob from the canvas')
      callback(null)
    }
  }, 'image/png', 1)
}

export const isLiteralNumber = (value) => typeof value === 'number' || (typeof value === 'string' && !Number.isNaN(Number(value)))

// MM DD, YYYY
export const formattedDate = (origDate) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  const date = new Date(origDate)

  return date.toLocaleDateString('en-US', options)
}

export const convertToOwnTimeZone = (origDate, format = '') => {
  const utcDate = new Date(origDate + 'Z')

  const localDate = new Date(utcDate.toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }))

  const pad = (n) => String(n).padStart(2, '0')

  let localDateTime = ''
  if (format === 'Y.m.d.H:i') {
    localDateTime = `${localDate.getFullYear()}.${pad(localDate.getMonth() + 1)}.${pad(localDate.getDate())}.${pad(localDate.getHours())}:${pad(localDate.getMinutes())}`
  } else if (format === 'Y.m.d') {
    localDateTime = `${localDate.getFullYear()}.${pad(localDate.getMonth() + 1)}.${pad(localDate.getDate())}`
  } else if (format === 'newDate') {
    localDateTime = localDate
  } else if (format === 'H:i') {
    localDateTime = `${pad(localDate.getHours())}:${pad(localDate.getMinutes())}`
  } else {
    localDateTime = `${localDate.getFullYear()}-${pad(localDate.getMonth() + 1)}-${pad(localDate.getDate())} ${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:${pad(localDate.getSeconds())}`
  }

  return localDateTime
}

export const isNowGreaterOrEqual = (origDate) => {
  const converted = convertToOwnTimeZone(origDate)

  const isoString = converted.replace(' ', 'T')

  const targetDate = new Date(isoString)
  const now = new Date()

  return now >= targetDate
}

export const base64ToFile = (base64String, strict = false) => {
  if ((base64String.split(',')).length !== 2 && strict) {
    return null
  }

  const [header, base64Data] = base64String.split(',')
  const mimeType = header.match(/:(.*?);/)[1]

  const byteCharacters = atob(base64Data)
  const byteNumbers = new Uint8Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  const blob = new Blob([byteNumbers], { type: mimeType })

  return new File([blob], 'image', { type: mimeType })
}

export const isIOS = () => /iPhone/.test(navigator.userAgent) && !window.MSStream

export const isMobile = () => /iPhone|Android.*Mobile|Windows Phone|webOS|BlackBerry/i.test(navigator.userAgent)

export const postTypeStatus = ({
  item,
  noLabel = false,
  getCategOnly = false
}) => {
  if (item.is_archived) {
    if (getCategOnly) {
      return 'Archived'
    }

    if (item?.has_repost) {
      return convertToOwnTimeZone(item.created_at, 'Y.m.d.H:i') + ' Archived Repost'
    }

    return convertToOwnTimeZone(item.updated_at, 'Y.m.d.H:i') + ' Archived'
  }

  if (
    item.is_planned
    && item.planned_at
    && new Date(item.planned_at + 'Z').toISOString() > new Date().toISOString()
  ) {
    if (getCategOnly) {
      return 'Scheduled'
    }

    return convertToOwnTimeZone(item.planned_at, 'Y.m.d.H:i') + ' Scheduled'
  }

  if (getCategOnly) {
    return 'Published'
  }

  if (item.created_at !== item.updated_at) {
    return convertToOwnTimeZone(item.updated_at, 'Y.m.d.H:i') + ' Modified'
  }

  if (noLabel) {
    return convertToOwnTimeZone(item.created_at, 'Y.m.d.H:i')
  }

  return convertToOwnTimeZone(item.created_at, 'Y.m.d.H:i') + ' Published'
}

export const convertToUTC = (datetime) => {
  let date = new Date(datetime)
  return date.toISOString().slice(0, 19).replace('T', ' ')
}

export const isNotMobile = () => (
  window.innerWidth > 600
  && window.innerHeight > 800
) || (
  window.innerWidth > 800
  && window.innerHeight > 600
)

export const chatCounter = (numUnread) => {
  if (numUnread > 9 || numUnread === '99+') {
    return '9+'
  }

  if (numUnread > 0) {
    return numUnread
  }

  return ''
}

export const showCoordParams = (setSearchParams) => {
  const latLS = localStorage.getItem('lat')
  const lngLS = localStorage.getItem('lng')
  const zoomLS = localStorage.getItem('zoom')

  if (latLS && lngLS && zoomLS) {
    setSearchParams({
      lat: latLS,
      lng: lngLS,
      zoom: zoomLS
    })
  }
}

export const capitalizeFirst = (str) => {
  if (!str) {
    return ''
  }

  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const differenceInMS = (datetime) => {
  const target = new Date(datetime.replace(' ', 'T'))
  const now = new Date()

  return target.getTime() - now.getTime()
}

export const engagementTimeFormat = (datetime) => {
  const diffMins = differenceInMS(convertToOwnTimeZone(datetime))

  if (diffMins > -60000) {
    return ENGAGEMENT_TIME_FORMAT.JustNow
  }

  if (diffMins > -3600000) {
    return ENGAGEMENT_TIME_FORMAT.ABitAgo
  }

  if (diffMins > -86400000) {
    return convertToOwnTimeZone(datetime, 'H:i')
  }

  if (diffMins > -172800000) {
    return ENGAGEMENT_TIME_FORMAT.Yesterday
  }

  return convertToOwnTimeZone(datetime, 'Y.m.d.H:i')
}

export const decimalToDMS = (decimal, type) => {
  // eslint-disable-next-line no-nested-ternary
  const dir = type === 'lat'
    ? (decimal >= 0 ? 'N' : 'S')
    : (decimal >= 0 ? 'E' : 'W')

  const abs = Math.abs(decimal)
  const deg = Math.floor(abs)
  const minFloat = (abs - deg) * 60
  const min = Math.floor(minFloat)
  const sec = ((minFloat - min) * 60).toFixed(2)

  return `${deg}°${min}'${sec}"${dir}`
}

export const convertDatetoICSFormat = (date) => {
  const year = date?.getFullYear()
  const month = date?.getMonth() + 1
  const day = date?.getDate()
  const hour = date?.getHours()
  const minute = date?.getMinutes()

  const format = [year, month, day, hour, minute]

  return format
}

export const toYouTubeEmbed = (url) => {
  try {
    const { searchParams, hostname, pathname } = new URL(url)

    if (
      pathname.startsWith('/shorts/')
      && (
        hostname.includes('youtube.com')
        || hostname === 'youtu.be'
      )
    ) {
      return url.replace('/shorts/', '/embed/')
    }

    if (hostname.includes('youtube.com') && searchParams.has('v')) {
      return `https://www.youtube.com/embed/${searchParams.get('v')}`
    }

    if (hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${pathname}`
    }

    if (hostname.includes('youtube.com') && pathname.startsWith('/embed/')) {
      return url
    }

    return url
  } catch {
    return url
  }
}

export const toOdyseeEmbed = (url) => {
  try {
    const { hostname, pathname } = new URL(url)
    if (!hostname.includes('odysee.com')) return url

    const decodedPath = decodeURIComponent(pathname)

    if (decodedPath.startsWith('/$/embed/')) return `https://odysee.com${decodedPath}`
    return `https://odysee.com/$/embed${decodedPath}`
  } catch {
    return url
  }
}

export const deterministicOffset = (value) => {
  const str = String(value)
  let hash = 0

  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }

  return (((Math.abs(hash) % 10000) / 10000) - 0.5) * 0.000002
}
